// Core scoring engine for KajChi nutrition evaluation

import type {
  Basket,
  Food,
  Nutrient,
  NutrientIndicator,
  FoodOption,
  BasketTotals,
  ScoringContext,
  ScoreResult,
  EstimatedTargets,
  CustomNutrientConstraints,
  CustomPortions,
  CustomPurchaseInfo
} from '../types.js'
import { scoreNutrient } from './algorithms.js'

/**
 * Main scoring engine - evaluates a basket and returns complete results
 */
export function evaluateBasket(context: ScoringContext): ScoreResult {
  const { basket, foods, nutrients, estimatedConstraints, customConstraints, customPortions, customPurchaseInfo } = context

  // Merge custom portions with base foods
  let effectiveFoods = applyCustomPortions(foods, customPortions)

  // Merge custom purchase info
  effectiveFoods = applyCustomPurchaseInfo(effectiveFoods, customPurchaseInfo)

  // Create food lookup map with effective foods
  const foodMap = new Map(effectiveFoods.map(f => [f.id, f]))

  // Apply estimated constraints (from user profile), then manual overrides on top
  let effectiveNutrients = applyEstimatedConstraints(nutrients, estimatedConstraints)
  effectiveNutrients = applyCustomConstraints(effectiveNutrients, customConstraints)

  // Calculate nutrient totals
  const nutrientTotals = calculateNutrientTotals(basket, foodMap, effectiveNutrients)

  // Calculate basket totals (weight, price)
  const totals = calculateBasketTotals(basket, foodMap)

  // Evaluate each nutrient (pass/fail only)
  const indicators = evaluateNutrients(nutrientTotals, effectiveNutrients)

  // Count passed nutrients as simple "score"
  const passedCount = indicators.filter(i => i.passing).length
  totals.score = passedCount

  // Generate food options (simplified, no score deltas)
  const foodOptions = generateFoodOptions(basket, effectiveFoods)

  return {
    indicators,
    foodOptions,
    totals,
    overallScore: passedCount
  }
}

/**
 * Calculate total nutrient amounts from basket
 */
function calculateNutrientTotals(
  basket: Basket, 
  foodMap: Map<string, Food>,
  nutrients: Nutrient[]
): Record<number, number> {
  const totals: Record<number, number> = {}
  
  // Initialize all nutrients to 0
  nutrients.forEach(nutrient => {
    totals[nutrient.id] = 0
  })
  
  // Sum nutrients from all foods in basket
  for (const [foodId, grams] of Object.entries(basket)) {
    if (grams <= 0) continue

    const food = foodMap.get(foodId)
    if (!food) continue

    // Use grams directly from basket
    const totalFoodWeight = grams

    // Add nutrients from this food
    for (const [nutrientIdStr, amountPer100g] of Object.entries(food.nutrients)) {
      const nutrientId = parseInt(nutrientIdStr)
      if (totals[nutrientId] !== undefined) {
        // Convert from per-100g to actual amount
        const actualAmount = (amountPer100g * totalFoodWeight) / 100
        totals[nutrientId] += actualAmount
      }
    }
  }
  
  return totals
}

/**
 * Calculate basket totals (weight, price, score)
 */
function calculateBasketTotals(basket: Basket, foodMap: Map<string, Food>): BasketTotals {
  let totalWeight = 0
  let totalPrice = 0

  for (const [foodId, grams] of Object.entries(basket)) {
    if (grams <= 0) continue

    const food = foodMap.get(foodId)
    if (!food) continue

    // Add weight directly from basket
    totalWeight += grams

    // Calculate price based on grams (guard against zero divisors)
    if (food.purchaseRawGrams > 0 && food.portionGrams > 0) {
      const pricePerGram = food.purchasePrice / food.purchaseRawGrams
      const gramsPrice = pricePerGram * (grams * food.rawPortionGrams / food.portionGrams)
      totalPrice += gramsPrice
    }
  }
  
  return {
    weight: totalWeight,
    price: totalPrice,
    score: 0 // Will be set by caller
  }
}

/**
 * Evaluate all nutrients and create indicators (pass/fail only)
 */
function evaluateNutrients(
  nutrientTotals: Record<number, number>,
  nutrients: Nutrient[]
): NutrientIndicator[] {
  return nutrients.map(nutrient => {
    const amount = nutrientTotals[nutrient.id] || 0

    // Check pass/fail for this nutrient
    const result = scoreNutrient({
      amount,
      nutrient
    })

    return {
      nutrientId: nutrient.id,
      name: nutrient.name,
      unit: nutrient.unit,
      value: amount,
      ...(nutrient.min !== undefined && { min: nutrient.min }),
      ...(nutrient.max !== undefined && { max: nutrient.max }),
      ...(nutrient.target !== undefined && { target: nutrient.target }),
      algorithm: nutrient.algorithm,
      priority: nutrient.priority,
      category: nutrient.category,
      passing: result.passing,
      score: result.passing ? 1 : 0
    }
  })
}


/**
 * Calculate passed nutrient count (simplified version)
 */
export function calculateScoreOnly(context: ScoringContext): number {
  const { basket, foods, nutrients, estimatedConstraints, customConstraints } = context
  const foodMap = new Map(foods.map(f => [f.id, f]))
  let effectiveNutrients = applyEstimatedConstraints(nutrients, estimatedConstraints)
  effectiveNutrients = applyCustomConstraints(effectiveNutrients, customConstraints)
  const nutrientTotals = calculateNutrientTotals(basket, foodMap, effectiveNutrients)
  const indicators = evaluateNutrients(nutrientTotals, effectiveNutrients)
  return indicators.filter(i => i.passing).length
}

/**
 * Generate food options (simplified, no score deltas)
 */
function generateFoodOptions(
  basket: Basket,
  foods: Food[]
): FoodOption[] {
  return foods.map(food => {
    const inBasket = food.id in basket
    const grams = inBasket ? (basket[food.id] ?? 0) : 0

    return {
      food,
      existingGrams: grams,
      totalWeightInBasket: grams,
      inBasket
    }
  })
}

/**
 * Sort food options by category and name
 */
export function sortFoodsByCategoryAndName(foodOptions: FoodOption[]): FoodOption[] {
  return [...foodOptions].sort((a, b) => {
    if (a.food.category !== b.food.category) {
      return a.food.category.localeCompare(b.food.category)
    }
    return a.food.name.localeCompare(b.food.name)
  })
}

/**
 * Filter foods for basket view (foods with portions > 0)
 */
export function getBasketFoods(foodOptions: FoodOption[], basketOrder?: string[]): FoodOption[] {
  return foodOptions
    .filter(option => option.inBasket === true)
    .sort((a, b) => {
      // Sort by basketOrder if provided
      if (basketOrder && basketOrder.length > 0) {
        const indexA = basketOrder.indexOf(a.food.id)
        const indexB = basketOrder.indexOf(b.food.id)

        // Both in order: sort by their order position
        if (indexA !== -1 && indexB !== -1) {
          return indexA - indexB
        }
        // Only A in order: A comes first
        if (indexA !== -1) return -1
        // Only B in order: B comes first
        if (indexB !== -1) return 1
        // Neither in order: fall through to alphabetical sort
      }

      // Fallback: Sort by category then name
      if (a.food.category !== b.food.category) {
        return a.food.category.localeCompare(b.food.category)
      }
      return a.food.name.localeCompare(b.food.name)
    })
}


/**
 * Apply estimated constraints from the nutrient estimator (user profile based).
 * This is the base layer — manual overrides are applied on top via applyCustomConstraints.
 */
function applyEstimatedConstraints(
  baseNutrients: Nutrient[],
  estimatedConstraints?: Record<number, EstimatedTargets>
): Nutrient[] {
  if (!estimatedConstraints || Object.keys(estimatedConstraints).length === 0) {
    return baseNutrients
  }

  return baseNutrients.map(nutrient => {
    const estimated = estimatedConstraints[nutrient.id]
    if (!estimated) return nutrient

    const merged: Nutrient = {
      ...nutrient,
      algorithm: estimated.algorithm
    }
    if (estimated.min !== null) merged.min = estimated.min
    if (estimated.recommended !== null) merged.target = estimated.recommended
    if (estimated.ul !== null) merged.max = estimated.ul
    return merged
  })
}

/**
 * Apply custom constraints to nutrients, creating effective nutrient configurations
 */
function applyCustomConstraints(
  baseNutrients: Nutrient[],
  customConstraints?: CustomNutrientConstraints
): Nutrient[] {
  if (!customConstraints || Object.keys(customConstraints).length === 0) {
    return baseNutrients
  }

  return baseNutrients.map(nutrient => {
    const customConstraint = customConstraints[nutrient.id]
    if (!customConstraint) {
      return nutrient
    }

    const merged: Nutrient = {
      ...nutrient,
      algorithm: customConstraint.algorithm ?? nutrient.algorithm
    }
    const min = customConstraint.min ?? nutrient.min
    const max = customConstraint.max ?? nutrient.max
    const target = customConstraint.target ?? nutrient.target
    if (min !== undefined) merged.min = min
    if (max !== undefined) merged.max = max
    if (target !== undefined) merged.target = target
    return merged
  })
}

/**
 * Apply custom portions to foods, creating effective food configurations
 */
function applyCustomPortions(
  baseFoods: Food[],
  customPortions?: CustomPortions
): Food[] {
  if (!customPortions || Object.keys(customPortions).length === 0) {
    return baseFoods
  }

  return baseFoods.map(food => {
    const customPortion = customPortions[food.id]
    if (!customPortion) {
      return food
    }

    // Calculate scaling ratio to maintain raw-to-cooked proportion
    const ratio = customPortion / food.portionGrams

    // Merge custom portion with base food, scaling rawPortionGrams proportionally
    return {
      ...food,
      portionGrams: customPortion,
      rawPortionGrams: food.rawPortionGrams * ratio
    }
  })
}

/**
 * Apply custom purchase info to foods, creating effective food configurations
 */
function applyCustomPurchaseInfo(
  baseFoods: Food[],
  customPurchaseInfo?: CustomPurchaseInfo
): Food[] {
  if (!customPurchaseInfo || Object.keys(customPurchaseInfo).length === 0) {
    return baseFoods
  }

  return baseFoods.map(food => {
    const customInfo = customPurchaseInfo[food.id]
    if (!customInfo) {
      return food
    }

    // Merge custom purchase info with base food
    return {
      ...food,
      purchaseRawGrams: customInfo.purchaseRawGrams,
      purchasePrice: customInfo.purchasePrice
    }
  })
}